import { createSlice } from '@reduxjs/toolkit'
import { cloneDeep, uniqueId } from 'lodash-es'
import { updateObject } from './utility'

const initialState = {
  outlet: {},
  shift: {},
  /** Cache detail produk custom: { productId, unitId, data } — dipakai agar tidak selalu hit API getFinanceProduct */
  customProduct: {},
  invoice_types: [],
  payment_type: [
    {
      id: 1,
      name: 'Tunai',
    },
    {
      id: 2,
      name: 'E-Wallet',
    },
    {
      id: 3,
      name: 'Online Delivery',
    },
    {
      id: 4,
      name: 'EDC',
    },
    {
      id: 5,
      name: 'E-Commerce',
    },
    {
      id: 6,
      name: 'Lainnya',
    },
    {
      id: 7,
      name: 'Transfer Bank',
    },
    {
      id: 8,
      name: 'Deposit',
    },
    {
      id: 9,
      name: 'QRIS',
    },
  ],
  pos_outlet_options: {},
  closeShiftModalOpen: false,
  closeShiftModalSuccess: {
    open: false,
    data: null,
  },
  hardwares: {
    printers: [],
  },
  isLocked: false,
  session: null, // Menyimpan state transaksi untuk session storage
  // Pengaturan lokal per-device (tanpa BE): hitung & tampilkan jumlah cetak struk.
  printReceiptCountActive: false,
  // Pengaturan lokal per-device (tanpa BE): bank tujuan Transfer yang diingat di kasir.
  // bank = object cashless_payment; autoApply = klik Transfer langsung tambah payment tanpa modal;
  // skipConfirm = jangan tampilkan modal konfirmasi "gunakan seterusnya?" lagi.
  transferBankPreference: {
    bank: null,
    autoApply: false,
    skipConfirm: false,
  },
  // Pengaturan lokal per-browser: Mode Kasir Zero-Loading (Beta) — katalog dibaca dari
  // IndexedDB & transaksi diantre lalu disinkron di latar belakang.
  posZeroLoading: false,
  // Endpoint tenant yang katalog lokalnya sudah tersinkron (gate sync sekali per tenant).
  catalogSyncedEndpoint: '',
}

const slice = createSlice({
  name: 'cashier',
  initialState,
  reducers: {
    // Pengaturan Zero-Loading per-browser tetap dipertahankan saat logout (scope data
    // lokal dicek per endpoint & per user, jadi aman dipakai akun lain).
    resetCashier: (state) => ({
      ...initialState,
      posZeroLoading: state.posZeroLoading,
      catalogSyncedEndpoint: state.catalogSyncedEndpoint,
    }),
    setOutlet: (state, { payload }) => updateObject(state, { outlet: payload }),
    setShift: (state, { payload }) => updateObject(state, { shift: payload }),
    setInvoiceTypes: (state, { payload }) => updateObject(state, { invoice_types: payload }),
    setPaymentType: (state, { payload }) => updateObject(state, { cashless_payments: payload }),
    setPosOutletOptions: (state, { payload }) => {
      // IMPORTANT: Ensure outlet_id is preserved in hardwares.printers
      // If printers exist but don't have outlet_id, add it from current outlet
      const currentPrinters = state.hardwares.printers || []
      const updatedPrinters = currentPrinters.map((printer) => {
        // If printer doesn't have outlet_id but outlet is available, add it
        if (!printer.outlet_id && state.outlet?.id) {
          return { ...printer, outlet_id: state.outlet.id }
        }
        return printer
      })

      return updateObject(state, {
        pos_outlet_options: payload,
        hardwares: {
          ...state.hardwares,
          printers: updatedPrinters,
        },
      })
    },
    setCloseShiftModalOpen: (state, { payload }) =>
      updateObject(state, { closeShiftModalOpen: payload }),
    setCloseShiftModalSuccess: (state, { payload }) =>
      updateObject(state, {
        closeShiftModalSuccess: { ...state.closeShiftModalSuccess, ...payload },
      }),
    updateOutletOptions: (state, { payload }) => {
      let currentData = cloneDeep(state.pos_outlet_options[payload.key])
      if (currentData) {
        currentData[payload.outlet_id] = payload.value
      } else {
        currentData = { [payload.outlet_id]: payload.value }
      }
      return updateObject(state, {
        pos_outlet_options: {
          ...state.pos_outlet_options,
          [payload.key]: currentData,
        },
      })
    },
    updatePrintStyleLogoBase64: (state, { payload }) => {
      const outletId = `${payload.outlet_id}`
      const currents = cloneDeep(state.pos_outlet_options.print_style_logo_base64 || {})
      currents[outletId] = payload.base64Image

      return updateObject(state, {
        pos_outlet_options: {
          ...state.pos_outlet_options,
          print_style_logo_base64: currents,
        },
      })
    },
    mutatePrinter: (state, { payload }) => {
      const printers = cloneDeep(state.hardwares.printers) || []
      const _payload = cloneDeep(payload)

      // Ensure outlet_id is preserved when updating existing printer
      if (_payload.id) {
        const findIndex = printers.findIndex((row) => row.id === _payload.id)
        if (findIndex >= 0 && printers[findIndex]) {
          // Preserve outlet_id from existing printer if not present in payload
          if (!_payload.outlet_id && printers[findIndex].outlet_id) {
            _payload.outlet_id = printers[findIndex].outlet_id
          }
        }
      }

      if (!_payload.id) {
        _payload.id = uniqueId()
        printers.push(_payload)
      } else {
        const findIndex = printers.findIndex((row) => row.id === _payload.id)
        if (printers[findIndex || 0]) {
          printers[findIndex || 0] = _payload
        }
      }

      return updateObject(state, {
        hardwares: {
          ...state.hardwares,
          printers,
        },
      })
    },
    deletePrinter: (state, { payload }) => {
      const findData = state.hardwares?.printers?.find((row) => row.id === payload)

      if (!findData) {
        return state
      }

      const printers = state.hardwares?.printers?.filter((row) => row.id !== payload) || []

      const hardwares = {
        ...state.hardwares,
        printers,
      }

      return updateObject(state, {
        hardwares,
      })
    },
    setLockScreen: (state, { payload }) => updateObject(state, { isLocked: payload }),
    setSession: (state, { payload }) => updateObject(state, { session: payload }),
    clearSession: (state) => updateObject(state, { session: null }),
    setCustomProductDetail: (state, { payload }) =>
      updateObject(state, { customProduct: payload || {} }),
    setPrintReceiptCountActive: (state, { payload }) =>
      updateObject(state, { printReceiptCountActive: payload }),
    setTransferBankPreference: (state, { payload }) =>
      updateObject(state, {
        transferBankPreference: { ...state.transferBankPreference, ...payload },
      }),
    resetTransferBankPreference: (state) =>
      updateObject(state, { transferBankPreference: initialState.transferBankPreference }),
    setPosZeroLoading: (state, { payload }) => updateObject(state, { posZeroLoading: !!payload }),
    setCatalogSyncedEndpoint: (state, { payload }) =>
      updateObject(state, { catalogSyncedEndpoint: payload || '' }),
  },
})

export const {
  resetCashier,
  setOutlet,
  setShift,
  setInvoiceTypes,
  setPaymentType,
  setPosOutletOptions,
  setCloseShiftModalOpen,
  updateOutletOptions,
  mutatePrinter,
  deletePrinter,
  setCloseShiftModalSuccess,
  setLockScreen,
  setSession,
  clearSession,
  updatePrintStyleLogoBase64,
  setCustomProductDetail,
  setPrintReceiptCountActive,
  setPosZeroLoading,
  setCatalogSyncedEndpoint,
  setTransferBankPreference,
  resetTransferBankPreference,
} = slice.actions

export default slice
